<script lang="ts" setup>
import { computed, inject, onBeforeMount, onMounted, Ref, ref } from 'vue';
import { useRouter } from 'vue-router';
import { CurrentUser, UserRoleEnum } from 'tir-pm-user-core';
import { TirPmSearchInput, TirPmSearchInputContentLine } from 'tir-pm-search-input';
import {
  TirPmIconView,
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirCheckbox,
  TirPmNotificationEventBus,
  TirPmNotificationModel,
  TirPmPopover,
  PopoverPositionEnum,
  PopoverColorEnum,
  TirPmTooltipElement,
} from 'tir-components';
import { TrashIcon } from 'tir-style-system/icons/outline';

import { AdminModel, SaveAdminsModel, UserModel } from './models';
import { ProjectModel } from '@/models';
import { AdapterService } from './adapter';
import { DeleteProjectAdminModal } from './components';

/**Адаптер */
const adapter = new AdapterService();
/**Маршруты */
const router = useRouter();

// #region refs
/**Ссылка на модальное окно удаления администратора из списка */
const $deleteAdminModal = ref<InstanceType<typeof DeleteProjectAdminModal>>();
/**Информация о проекте */
const project = inject('project') as Ref<ProjectModel>;
/**Загрузка при инициализации компонента */
const isInitLoading = ref(false);
/**Строка поиска */
const searchStr = ref<string>();
/**Список администраторов */
const admins = ref(new Array<AdminModel>());
/**Список пользователей */
const users = ref(new Array<UserModel>());
// #endregion refs

// #region computed
/**Владелец */
const isOwner = computed(() => CurrentUser.Roles.includes(UserRoleEnum.Owner));
/**Админ проекта */
const isProjectAdmin = computed(() => project.value.Admins.some((a) => a.Id == CurrentUser.Id));
/**Отсортированный список администраторов */
const sortAdmins = computed(() =>
  admins.value.sort((a, b) => a.FirstName.localeCompare(b.FirstName)),
);
/**Видимые пользователи в списке на добавление админов проекта */
const visibleUsers = computed(() => {
  let _users = users.value.filter((user) => !admins.value.some((admin) => user.Id === admin.Id));

  if (searchStr.value?.trim()) {
    _users = _users.filter((user) =>
      user.Initials.toLocaleLowerCase().includes(searchStr.value.trim().toLocaleLowerCase()),
    );
  }

  return _users.sort((a, b) => a.Initials.localeCompare(b.Initials));
});
// #endregion computed

// #region Хуки жизненного цикла
onMounted(() => {
  getData();
});
/**Перед рендером */
onBeforeMount(() => {
  if (!isOwner.value && !isProjectAdmin.value) {
    router.push('/');
  }
});
// #endregion Хуки жизненного цикла

// #region Функции
/**
 * Добавить уведомление
 * @param message Сообщение
 * @param isError Успех/ошибка
 */
const addNotification = (message: string, isError: boolean = false) => {
  TirPmNotificationEventBus.emit(
    'add',
    new TirPmNotificationModel({
      title: message,
      type: isError ? 'error' : 'success',
      group: 'general',
      life: isError ? 5000 : 1000,
      closable: true,
    }),
  );
};
/**
 * * Добавить администратора проекта
 * @param user Пользователь
 */
const addAdmin = async (user: UserModel) => {
  const admin = new AdminModel(user);
  const request = new SaveAdminsModel({
    ProjectId: project.value.Id,
    Admins: [...admins.value, admin].map((admin) => ({
      Id: admin.Id,
      CanCustomizeOrder: admin.CanCustomizeOrder,
    })),
    RemoveQueueTasksAccess: false,
    RemoveTaskObserver: false,
    RemoveTaskPerformer: false,
  });
  const resp = await adapter.saveProjectAdmins(request);
  if (resp.IsSuccess) {
    admins.value.push(admin);
    addNotification('Список успешно обновлен', false);
  } else {
    addNotification(resp.ErrorMessage ?? 'Произошла ошибка');
    setTimeout(() => getAdmins(), 1000); // Актуализация списка через 500 мс после ошибки
  }
};

/**
 * Обновить флаг возможности формирования очередности
 * @param admin Админ
 * @param canCustomizeOrder Значение
 */
const updateCanCustomizeOrder = async (admin: AdminModel, canCustomizeOrder: boolean) => {
  const index = admins.value.findIndex((_admin) => admin.Id === _admin.Id);
  if (index === -1) return;

  admins.value[index].CanCustomizeOrder = canCustomizeOrder;
  const request = new SaveAdminsModel({
    ProjectId: project.value.Id,
    Admins: admins.value.map((admin) => ({
      Id: admin.Id,
      CanCustomizeOrder: admin.CanCustomizeOrder,
    })),
    RemoveQueueTasksAccess: false,
    RemoveTaskObserver: false,
    RemoveTaskPerformer: false,
  });
  const resp = await adapter.saveProjectAdmins(request);
  if (resp.IsSuccess) {
    addNotification('Список успешно обновлен', false);
  } else {
    addNotification(resp.ErrorMessage ?? 'Произошла ошибка');
    setTimeout(() => getAdmins(), 1000); // Актуализация списка через 1 секунду после ошибки
  }
};
/**
 * Удалить администратора из локального списка
 * @param adminId ID администратора
 */
const deleteAdminLocal = (adminId: number) => {
  admins.value = admins.value.filter((admin) => admin.Id !== adminId);
};
/**
 * При успешном удалении администратора из списка
 * @param adminId ID администратора
 */
const onSuccessDeleteAdmin = (adminId: number) => {
  deleteAdminLocal(adminId);
};

/**Получить список администраторов */
const getAdmins = async () => {
  const resp = await adapter.getProjectAdmins(project.value.Id);

  if (resp.IsSuccess) {
    admins.value = resp.Value;
  }
};
/**Получить список пользователей для назначение админов проекта */
const getUsers = async () => {
  const resp = await adapter.getUsersAssignProjectAdmins();

  if (resp.IsSuccess) {
    users.value = resp.Value;
  }
};
/**Получить данные */
const getData = async () => {
  isInitLoading.value = true;
  await Promise.all([getAdmins(), getUsers()]);
  isInitLoading.value = false;
};
// #endregion Функции
</script>

<template>
  <div class="project-settings-admin-page">
    <DeleteProjectAdminModal
      ref="$deleteAdminModal"
      :admins="admins"
      :project-id="project.Id"
      @on-success-delete="onSuccessDeleteAdmin"
    />

    <h2 class="project-settings-admin-page__title">Администраторы</h2>
    <div class="project-settings-admin-page__content">
      <TirPmSearchInput
        style="width: 100%"
        label="Администраторы"
        placeholder=""
        :isWithPopup="true"
        v-model="searchStr"
        :isDisabled="!isOwner"
      >
        <template #content>
          <TirPmSearchInputContentLine
            v-for="user of visibleUsers"
            :key="user.Id"
            @click.stop="addAdmin(user)"
          >
            <template #leftSlot>
              <TirPmIconView
                :size="24"
                isRounded
                :isBordered="false"
                :colorCode="user.AvatarColor"
                :imageLoadUrl="user.Photo"
              >
                <template #mainSlot>
                  {{ user.AvatarInitials }}
                </template>
              </TirPmIconView>
            </template>
            {{ user.FirstName }} {{ user.MiddleName }} {{ user.LastName }}
          </TirPmSearchInputContentLine>
        </template>
      </TirPmSearchInput>
    </div>
    <div class="project-settings-admin-page__users-list">
      <div
        class="project-settings-admin-page__user-item"
        v-for="adminUser of sortAdmins"
        :key="adminUser.Id"
      >
        <div class="project-settings-admin-page__user-item-left">
          <TirPmIconView
            :size="48"
            isRounded
            :isBordered="false"
            :colorCode="adminUser.AvatarColor"
            :imageLoadUrl="adminUser.Photo"
          >
            <template #mainSlot>
              {{ adminUser.AvatarInitials }}
            </template>
          </TirPmIconView>
          <div class="project-settings-admin-page__sign-name">
            {{ adminUser.Initials }}
          </div>
        </div>
        <div class="project-settings-admin-page__user-item-right">
          <TirPmButton
            v-if="isOwner"
            class="project-settings-admin-page__delete-button"
            :size="TirPmButtonSizeEnum.Small"
            :variant="TirPmButtonVariantEnum.LinkState"
            @click="$deleteAdminModal?.open(adminUser)"
          >
            <template #leftSlot>
              <TrashIcon />
            </template>
          </TirPmButton>
          <TirPmTooltipElement>
            <TirCheckbox
              :isDisabled="!isOwner"
              :model-value="adminUser.CanCustomizeOrder"
              @update:model-value="updateCanCustomizeOrder(adminUser, $event)"
            />
            <template #content>
              <div class="project-settings-admin-page__checkbox-tooltip-content">
                Может формировать очередность задач
              </div>
            </template>
          </TirPmTooltipElement>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:map';
@import 'tir-style-system/expose-variables';
@import 'tir-style-system/expose-default-theme';
@import 'tir-style-system/core/mixins';

.project-settings-admin-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.75rem;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  &__user-item {
    padding: 0.75rem 1.25rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: background-color 0.2s ease-in;
    border-radius: 0.5rem;
  }

  &__user-item:hover {
    background-color: var(--neutral-translucent-5, map.get($colors, 'neutral-translucent-5'));
  }

  &__user-item-left {
    display: flex;
    align-items: center;
    gap: 0.625rem;
  }

  &__sign-name {
    font-size: 1rem;
    line-height: 1.5rem;
    color: var(--text-primary, map.get($colors, 'text-primary'));
  }

  &__user-item-right {
    display: flex;
    align-items: center;
    gap: 0.625rem;
  }

  &__delete-button {
    visibility: hidden;
  }

  &__user-item:hover &__delete-button {
    visibility: visible;
  }
  &__checkbox-tooltip-content {
    display: flex;
    justify-content: center;
    overflow-wrap: break-word;
    border-radius: 0.5rem;
    color: var(--text-primary-inverted, #e9eaeb);
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1rem;
  }
}
</style>

<style lang="scss">
.project-settings-admin-page__user-item-right {
  .button__left-slot {
    width: 1.5rem !important;
    height: 1.5rem !important;
  }
}
</style>
